import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey, useMutation, useQuery, useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  addEmailCaptureExclusion,
  getEmailCaptureSettings,
  getEmailThread,
  listEmailCaptureExclusions,
  listEmailThreads,
  removeEmailCaptureExclusion,
  updateEmailCaptureSettings,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetEmailCaptureSettingsRequestSchema,
  GetEmailCaptureSettingsResponseSchema,
  ListEmailCaptureExclusionsRequestSchema,
} from '@/gen/cortado/v1/cortado_pb'
import { GetEmailThreadRequestSchema } from '@/gen/cortado/v1/emails_pb'

import { invalidateCrossEntityEmailCaches } from './email-thread-cache'

/**
 * The organization's "Never capture" list (CPRO-783): addresses and domains
 * the connected inbox never saves. The team's own addresses are always
 * skipped and are not listed. Needs Manage Communications and EmailInbox.
 */
export function useEmailCaptureExclusions(organizationId: string, options: { enabled?: boolean } = {}) {
  const request = useMemo(
    () => create(ListEmailCaptureExclusionsRequestSchema, { organizationId }),
    [organizationId],
  )
  const query = useQuery(listEmailCaptureExclusions, request, {
    enabled: options.enabled !== false && !!organizationId,
    staleTime: 30 * 1000,
  })
  return {
    exclusions: query.data?.exclusions ?? [],
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
  }
}

function useInvalidateExclusionList() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  return {
    queryClient,
    transport,
    invalidateList: () =>
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({ schema: listEmailCaptureExclusions, transport, cardinality: undefined }),
      }),
  }
}

/**
 * Adds a "Never capture" entry. When the request also wiped a conversation
 * (`wipeThreadId`) and the server deleted messages, every thread view that
 * could be showing them is refreshed too.
 */
export function useAddEmailCaptureExclusion() {
  const { queryClient, transport, invalidateList } = useInvalidateExclusionList()
  return useMutation(addEmailCaptureExclusion, {
    onSuccess: (response, variables) => {
      void invalidateList()
      if (!variables.wipeThreadId || response.wipedMessageCount <= 0) return
      void queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getEmailThread,
          transport,
          input: create(GetEmailThreadRequestSchema, { id: variables.wipeThreadId }),
          cardinality: 'finite',
        }),
      })
      void queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({ schema: listEmailThreads, transport, cardinality: undefined }),
      })
      // Entity Conversations tabs and the per-parent email history.
      invalidateCrossEntityEmailCaches(queryClient, transport)
    },
  })
}

/** Removes a "Never capture" entry. */
export function useRemoveEmailCaptureExclusion() {
  const { invalidateList } = useInvalidateExclusionList()
  return useMutation(removeEmailCaptureExclusion, {
    onSuccess: () => {
      void invalidateList()
    },
  })
}

/**
 * The organization's capture settings (CPRO-787): whether mail from a
 * company's own domain is captured and linked to that company while it has
 * open work. Needs Manage Communications and EmailInbox.
 */
export function useEmailCaptureSettings(organizationId: string, options: { enabled?: boolean } = {}) {
  const request = useMemo(
    () => create(GetEmailCaptureSettingsRequestSchema, { organizationId }),
    [organizationId],
  )
  const query = useQuery(getEmailCaptureSettings, request, {
    enabled: options.enabled !== false && !!organizationId,
    staleTime: 30 * 1000,
  })
  return {
    settings: query.data?.settings,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
  }
}

/** Saves capture settings and writes the result into the read cache. */
export function useUpdateEmailCaptureSettings() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  return useMutation(updateEmailCaptureSettings, {
    onSuccess: (response, variables) => {
      queryClient.setQueryData(
        createConnectQueryKey({
          schema: getEmailCaptureSettings,
          transport,
          input: create(GetEmailCaptureSettingsRequestSchema, { organizationId: variables.organizationId }),
          cardinality: 'finite',
        }),
        create(GetEmailCaptureSettingsResponseSchema, { settings: response.settings }),
      )
    },
  })
}
